import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { appsAPI } from '../api/client';
import { getScreenshotUrl, getLogoUrl } from '../utils/helpers';
import UpvoteButton from '../components/app/UpvoteButton';
import { ExternalLink, Loader, ChevronLeft, ChevronRight, X } from 'lucide-react';

const AppDetailPage = () => {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [totalAppsCount, setTotalAppsCount] = useState(0);

  useEffect(() => {
    fetchApp();
    fetchTotalApps();
  }, [id]);

  const fetchApp = async () => {
    try {
      const response = await appsAPI.getById(id);
      setApp(response.data);
    } catch (err) {
      setError('App not found');
      console.error('Failed to fetch app:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalApps = async () => {
    try {
      const response = await appsAPI.getHomepage();
      const total = response.data.weekly_groups.reduce((sum, week) => sum + week.total_count, 0);
      setTotalAppsCount(total);
    } catch (err) {
      console.error('Failed to fetch total apps:', err);
    }
  };

  const handleVisitApp = async () => {
    try {
      await appsAPI.trackClick(app.id);
      window.open(app.app_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to track click:', error);
      window.open(app.app_url, '_blank', 'noopener,noreferrer');
    }
  };

  const openImageModal = (index) => {
    setModalImageIndex(index);
    setShowImageModal(true);
  };

  const nextImage = () => {
    setModalImageIndex((prev) => (prev + 1) % app.screenshots.length);
  };

  const prevImage = () => {
    setModalImageIndex((prev) => (prev - 1 + app.screenshots.length) % app.screenshots.length);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-dark-bg">
        <Loader className="w-12 h-12 animate-spin text-brand-yellow" />
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center bg-dark-bg">
        <h1 className="text-2xl font-bold text-white mb-4">App Not Found</h1>
        <p className="text-gray-400 mb-8">{error}</p>
        <Link to="/" className="text-brand-yellow hover:text-brand-yellow/90 font-medium">
          ← Back to Homepage
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg py-12">
      <div className="max-w-4xl mx-auto px-6 sm:px-8">
        {/* Header with Logo */}
        <div className="mb-8">
          <div className="flex items-start gap-6 mb-4">
            {/* App Logo */}
            {getLogoUrl(app) && (
              <div className="flex-shrink-0">
                <img
                  src={getLogoUrl(app)}
                  alt={app.app_name}
                  className="w-24 h-24 rounded-2xl border-2 border-dark-border"
                  loading="lazy"
                />
              </div>
            )}

            {/* Title and Actions */}
            <div className="flex-1">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h1 className="text-4xl font-bold text-white mb-3" data-testid="app-title">
                    {app.app_name}
                  </h1>
                  <p className="text-xl text-gray-400 leading-relaxed">{app.tagline}</p>
                </div>
                
                {/* Visit & Upvote Buttons */}
                <div className="flex gap-3 items-start">
                  <button
                    onClick={handleVisitApp}
                    className="px-6 py-3 bg-brand-yellow text-dark-bg rounded-lg hover:bg-brand-yellow/90 transition-colors font-bold flex items-center gap-2 whitespace-nowrap"
                    data-testid="visit-app-button"
                  >
                    Visit
                    <ExternalLink className="w-5 h-5" />
                  </button>
                  <UpvoteButton app={app} />
                </div>
              </div>
            </div>
          </div>

          {/* Categories - Left aligned with logo margin */}
          <div className="flex gap-2">
            <Link
              to={`/?l1=${app.category_l1_id}`}
              className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-900/30 text-blue-300 border border-blue-700/30 hover:border-blue-500/50"
            >
              {app.category_l1_name}
            </Link>
            <Link
              to={`/?l1=${app.category_l1_id}&l2=${app.category_l2_id}`}
              className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-900/30 text-purple-300 border border-purple-700/30 hover:border-purple-500/50"
            >
              {app.category_l2_name}
            </Link>
          </div>
        </div>

        {/* Description - No header */}
        <div className="mb-8">
          <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-wrap">{app.description}</p>
        </div>

        {/* Screenshots - Inline, No header */}
        {app.screenshots.length > 0 && (
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-4">
              {app.screenshots.map((screenshot, index) => (
                <div
                  key={screenshot.id}
                  onClick={() => openImageModal(index)}
                  className="cursor-pointer rounded-lg overflow-hidden border border-dark-border hover:border-brand-yellow/50 transition-all"
                >
                  <img
                    src={getScreenshotUrl(screenshot)}
                    alt={`Screenshot ${index + 1}`}
                    className="w-full h-auto object-cover hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* See All Apps Banner */}
        <Link
          to="/"
          className="block mb-8 p-6 bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-700/30 rounded-xl hover:border-brand-yellow/50 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white mb-1 group-hover:text-brand-yellow transition-colors">
                See all apps
              </h3>
              <p className="text-gray-400 text-sm">
                {totalAppsCount} apps listed on Emergent Showcase
              </p>
            </div>
            <div className="text-brand-yellow group-hover:translate-x-1 transition-transform">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </Link>
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setShowImageModal(false)}
        >
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-4 right-4 p-2 bg-dark-card rounded-full hover:bg-dark-bg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          <div className="relative max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={getScreenshotUrl(app.screenshots[modalImageIndex])}
              alt={`Screenshot ${modalImageIndex + 1}`}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              loading="lazy"
            />

            {app.screenshots.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 bg-dark-card/80 backdrop-blur-sm border border-dark-border rounded-full hover:bg-dark-bg transition-colors"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 bg-dark-card/80 backdrop-blur-sm border border-dark-border rounded-full hover:bg-dark-bg transition-colors"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>

                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-dark-card/80 backdrop-blur-sm rounded-full border border-dark-border">
                  <span className="text-white text-sm font-medium">
                    {modalImageIndex + 1} / {app.screenshots.length}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppDetailPage;
